import React, { useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { Box, CircularProgress, Typography } from '@mui/material';
import { useAuth } from '../context/AuthContext';
import { hasValidRole, isValidRole, normalizeRole } from '../utils/authorizationRules';
import Unauthorized from '../pages/Unauthorized';

/**
 * RoleBasedRoute component that checks if user has permission to access a route
 * 
 * This component:
 * 1. First validates that user has a valid role (CH, MP, ED, GM, ADMIN, SUPERADMIN)
 * 2. Then checks hasPermission function if provided
 * 3. If EITHER check fails and user has invalid role, redirects to /contact-admin
 * 4. If role is valid but permission check fails, shows Unauthorized
 * 
 * @param {Object} props
 * @param {React.ReactNode} props.children - The component to render if authorized
 * @param {Function} props.hasPermission - Function that receives user object and returns boolean
 * @param {boolean} props.showUnauthorized - If true, shows Unauthorized page instead of redirecting
 * @param {string} props.routePath - Current route path for logging purposes
 */
const RoleBasedRoute = ({ 
  children, 
  hasPermission, 
  showUnauthorized = true,
  routePath = 'unknown'
}) => {
  const { isAuthenticated, loading, user } = useAuth();

  // Validate user role on component render
  useEffect(() => {
    if (!loading && isAuthenticated && user && !hasValidRole(user)) {
      // console.warn('[RoleBasedRoute] User with invalid role attempted access:', {
        // role: user.role,
        // email: user.email,
        // routePath,
        // normalized: normalizeRole(user.role),
      // });
    }
  }, [loading, isAuthenticated, user, routePath]);

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          gap: 2,
        }}
      >
        <CircularProgress />
        <Typography variant="body1" color="text.secondary">
          Loading...
        </Typography>
      </Box>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Access pending restriction disabled — users allowed even if role not mapped.
  /*
  // CRITICAL: Check if user has valid role first
  // If role is not in our valid roles system, redirect to contact-admin
  if (!user || !user.role || !hasValidRole(user)) {
    // console.error('[RoleBasedRoute] User has invalid/missing role, redirecting to contact-admin:', {
      // userRole: user?.role,
      // email: user?.email,
      // routePath,
    // });
    // Do NOT show Unauthorized page for invalid roles - directly redirect to contact-admin
    return <Navigate to="/contact-admin" replace />;
  }
  */

  // Role is valid, now check specific permissions
  const hasAccess = hasPermission ? hasPermission(user) : true;

  if (!hasAccess) {
    // Role is valid but permission denied - show unauthorized or redirect
    if (showUnauthorized) {
      return <Unauthorized />;
    }
    // Access pending restriction disabled — users allowed even if role not mapped.
    // Redirect to contact-admin if permission denied
    // return <Navigate to="/contact-admin" replace />;
  }

  // User has valid role and permission - render component
  return children;
};

export default RoleBasedRoute;
